/**
 * Admin → App settings → Help & feedback (SA-1306 spec §3): the deployment's switches for the
 * Help & feedback panel and the extra instructions its answers follow. The model is on the AI
 * models page. Read-only without `app_feedback:manage`.
 */
import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Switch, Textarea } from '@/components/ui'
import { getSettings, putSettings, type HelpFeedbackSettings as Settings } from '@/services/helpFeedback/appFeedbackApi'
import { usePermissionStore } from '@/stores/permissionStore'

export const INSTRUCTIONS_MAX = 4000

export const SETTINGS_WORDS = {
  answers: 'Answer how-to questions',
  answersHelp: 'Off: the panel points to the Help & docs guide and offers to send the question to the team.',
  voice: 'Talking instead of typing',
  voiceHelp: 'Off: the panel has no microphone.',
  suggest: 'Suggest help after something fails',
  suggestHelp: 'Takes effect when suggestions arrive.',
  instructions: 'Extra instructions',
  instructionsHelp: 'Added to what the assistant is told before it answers a how-to question.',
  modelBefore: 'The model and key are chosen on ',
  modelLink: 'AI models',
  modelAfter: ' (Help and feedback assistant).',
  save: 'Save',
  saved: 'Saved.',
  loadFailed: "Couldn't load the Help & feedback settings.",
  saveFailed: "Couldn't save the Help & feedback settings.",
  readOnly: 'You can view these settings; changing them needs Manage Product Feedback.',
} as const

function useCanManage(): boolean {
  return usePermissionStore((s) => s.isLoaded && s.permissions.has('app_feedback:manage'))
}

export function HelpFeedbackSettings() {
  const canManage = useCanManage()
  const [settings, setSettings] = useState<Settings | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const ids = useId()

  useEffect(() => {
    let live = true
    getSettings()
      .then((s) => live && setSettings(s))
      .catch(() => live && setLoadFailed(true))
    return () => {
      live = false
    }
  }, [])

  if (loadFailed) return <p className="text-sm text-destructive">{SETTINGS_WORDS.loadFailed}</p>
  if (!settings) return null

  const edit = (patch: Partial<Settings>) => {
    setSettings((s) => (s ? { ...s, ...patch } : s))
    setMessage('')
  }

  const save = async () => {
    setSaving(true)
    setMessage('')
    try {
      setSettings(await putSettings(settings))
      setMessage(SETTINGS_WORDS.saved)
    } catch {
      setMessage(SETTINGS_WORDS.saveFailed)
    } finally {
      setSaving(false)
    }
  }

  const toggle = (key: 'answers' | 'voice' | 'suggest_after_failure', label: string, help: string) => (
    <div className="flex items-start justify-between gap-4">
      <div>
        <label htmlFor={`${ids}-${key}`} className="text-sm font-medium">{label}</label>
        <p id={`${ids}-${key}-help`} className="text-xs text-muted-foreground">{help}</p>
      </div>
      <Switch
        id={`${ids}-${key}`}
        aria-describedby={`${ids}-${key}-help`}
        checked={settings[key]}
        disabled={!canManage}
        onCheckedChange={(checked) => edit({ [key]: checked })}
      />
    </div>
  )

  return (
    <div className="space-y-5">
      {!canManage && <p className="text-sm text-muted-foreground">{SETTINGS_WORDS.readOnly}</p>}
      {toggle('answers', SETTINGS_WORDS.answers, SETTINGS_WORDS.answersHelp)}
      {toggle('voice', SETTINGS_WORDS.voice, SETTINGS_WORDS.voiceHelp)}
      {toggle('suggest_after_failure', SETTINGS_WORDS.suggest, SETTINGS_WORDS.suggestHelp)}

      <div className="space-y-1">
        <label htmlFor={`${ids}-instructions`} className="text-sm font-medium">{SETTINGS_WORDS.instructions}</label>
        <Textarea
          id={`${ids}-instructions`}
          aria-describedby={`${ids}-instructions-help`}
          rows={6}
          maxLength={INSTRUCTIONS_MAX}
          readOnly={!canManage}
          value={settings.extra_instructions}
          onChange={(e) => edit({ extra_instructions: e.target.value })}
        />
        <p id={`${ids}-instructions-help`} className="text-xs text-muted-foreground">
          {SETTINGS_WORDS.instructionsHelp} {settings.extra_instructions.length} / {INSTRUCTIONS_MAX}
        </p>
      </div>

      <p className="text-sm text-muted-foreground">
        {SETTINGS_WORDS.modelBefore}
        <Link to="/admin/ai-models" className="underline">{SETTINGS_WORDS.modelLink}</Link>
        {SETTINGS_WORDS.modelAfter}
      </p>

      {canManage && (
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving}>{SETTINGS_WORDS.save}</Button>
          <span role="status" className="text-sm text-muted-foreground">{message}</span>
        </div>
      )}
    </div>
  )
}
